// @ts-nocheck
/**
 * デッキ構築画面
 *
 * 所持カードからデッキを構築・編集する
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ATTRIBUTE_COLORS } from '../../utils/constants';
import { RARITY_COLORS, RARITY_NAMES, RARITIES } from '../data/constants';
import { collectionManager } from '../systems/collectionManager';
import { useIsMobile } from '../hooks/useMediaQuery';
import { KEYWORD_ABILITIES, getCardKeywords } from '../../engine/keywordAbilities';
import { CATEGORIES } from '../market/data/categories';
import DeckBuilderCardGrid from './DeckBuilderCardGrid';
import { EFFECT_LEVELS } from '../../styles/rarityEffects';
import { CardV2, RarityEffectLayer } from '../../components/cardV2';

// ========================================
// 定数
// ========================================

const DECK_SIZE = 40;
const MAX_COPIES = 3; // 同名カード最大枚数（公式ルール準拠）
const MAX_FORBIDDEN = 1; // 禁忌カード最大枚数

// キーワード・カテゴリ定義
const KEYWORDS = Object.values(KEYWORD_ABILITIES);
const CARD_CATEGORIES = CATEGORIES;

// ========================================
// スタイル定義
// ========================================

const styles = {
  container: {
    height: '100vh',
    background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a3a 50%, #0a0a1a 100%)',
    fontFamily: '"Noto Sans JP", "Hiragino Sans", sans-serif',
    color: '#e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(90deg, rgba(20,20,50,0.95) 0%, rgba(40,20,60,0.95) 50%, rgba(20,20,50,0.95) 100%)',
    borderBottom: '2px solid #6b4ce6',
    padding: '12px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 4px 20px rgba(107,76,230,0.3)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: 'rgba(107,76,230,0.3)',
    color: '#e0e0e0',
    transition: 'all 0.3s ease',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#a78bfa',
  },
  deckInfo: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  deckCount: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  // 左側: カード一覧
  cardListPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid rgba(107,76,230,0.3)',
    overflow: 'hidden',
  },
  filterBar: {
    padding: '12px',
    background: 'rgba(20,20,40,0.8)',
    borderBottom: '1px solid rgba(107,76,230,0.3)',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterSelect: {
    padding: '6px 12px',
    fontSize: '13px',
    borderRadius: '6px',
    border: '1px solid rgba(107,76,230,0.5)',
    background: '#1a1a2e',
    color: '#e0e0e0',
    cursor: 'pointer',
  },
  searchInput: {
    padding: '6px 12px',
    fontSize: '13px',
    borderRadius: '6px',
    border: '1px solid rgba(107,76,230,0.5)',
    background: '#1a1a2e',
    color: '#e0e0e0',
    flex: 1,
    minWidth: '150px',
  },
  cardGrid: {
    flex: 1,
    overflow: 'auto',
    padding: '12px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '8px',
    alignContent: 'start',
  },
  cardItem: {
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    position: 'relative',
  },
  cardCost: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#fff',
  },
  cardRarity: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    padding: '2px 4px',
    borderRadius: '3px',
    fontSize: '9px',
    fontWeight: 'bold',
    color: '#fff',
  },
  cardName: {
    fontSize: '10px',
    textAlign: 'center',
    color: '#fff',
    marginTop: '24px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  cardQuantity: {
    fontSize: '10px',
    color: '#ffffff',
    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
  },
  cardDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  // 中央: デッキ内容
  deckPanel: {
    width: '280px',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(20,20,40,0.5)',
    borderRight: '1px solid rgba(107,76,230,0.3)',
    overflow: 'hidden',
  },
  // 右側: カード詳細パネル
  detailPanel: {
    width: '300px',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(15,15,35,0.9)',
    overflow: 'auto',
  },
  detailEmpty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#606080',
    fontSize: '14px',
    textAlign: 'center',
    padding: '24px',
  },
  detailContent: {
    padding: '16px',
  },
  detailCardPreview: {
    width: '100%',
    aspectRatio: '3/4',
    maxHeight: '180px',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  detailCardHeader: {
    padding: '8px 10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(0,0,0,0.3)',
  },
  detailCardBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px',
  },
  detailCardName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
    marginBottom: '4px',
  },
  detailCardCategory: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.7)',
    textAlign: 'center',
  },
  detailSection: {
    marginBottom: '12px',
  },
  detailSectionTitle: {
    fontSize: '12px',
    color: '#a0a0a0',
    marginBottom: '6px',
    borderBottom: '1px solid rgba(107,76,230,0.3)',
    paddingBottom: '4px',
  },
  detailStatsRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '8px',
  },
  detailStatItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  detailStatLabel: {
    fontSize: '11px',
    color: '#a0a0a0',
  },
  detailStatValue: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
  },
  detailEffectText: {
    fontSize: '12px',
    color: '#e0e0e0',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
  },
  detailKeyword: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    background: 'rgba(157,76,230,0.3)',
    color: '#c9a0ff',
    fontSize: '11px',
    marginRight: '4px',
    marginBottom: '4px',
  },
  detailForbidden: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    background: 'rgba(255,100,100,0.3)',
    color: '#ff6b6b',
    fontSize: '11px',
    marginRight: '4px',
  },
  deckHeader: {
    padding: '12px',
    background: 'rgba(20,20,40,0.8)',
    borderBottom: '1px solid rgba(107,76,230,0.3)',
  },
  deckNameInput: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid rgba(107,76,230,0.5)',
    background: '#1a1a2e',
    color: '#e0e0e0',
    marginBottom: '8px',
    boxSizing: 'border-box',
  },
  deckStats: {
    display: 'flex',
    gap: '12px',
    fontSize: '12px',
    color: '#a0a0a0',
  },
  deckList: {
    flex: 1,
    overflow: 'auto',
    padding: '8px',
  },
  deckCardItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '4px',
    marginBottom: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  deckCardCost: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#fff',
    flexShrink: 0,
  },
  deckCardName: {
    flex: 1,
    fontSize: '12px',
    color: '#e0e0e0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  deckCardCount: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#a78bfa',
  },
  deckCardRarity: {
    fontSize: '10px',
    padding: '2px 4px',
    borderRadius: '3px',
    color: '#fff',
  },
  deckFooter: {
    padding: '12px',
    background: 'rgba(20,20,40,0.8)',
    borderTop: '1px solid rgba(107,76,230,0.3)',
    display: 'flex',
    gap: '8px',
  },
  saveButton: {
    flex: 1,
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    color: '#fff',
    transition: 'all 0.3s ease',
  },
  saveButtonDisabled: {
    background: 'rgba(100,100,100,0.5)',
    cursor: 'not-allowed',
  },
  clearButton: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255,100,100,0.5)',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: 'rgba(255,100,100,0.2)',
    color: '#ff6b6b',
    transition: 'all 0.3s ease',
  },
  errorMessage: {
    padding: '8px 12px',
    background: 'rgba(255,100,100,0.2)',
    border: '1px solid rgba(255,100,100,0.5)',
    borderRadius: '6px',
    color: '#ff6b6b',
    fontSize: '12px',
    marginTop: '8px',
  },
  emptyMessage: {
    padding: '24px',
    textAlign: 'center',
    color: '#808080',
    fontSize: '14px',
  },
};

// ========================================
// メインコンポーネント
// ========================================

const DeckBuilder = ({
  playerData,
  allCards,
  cardValueMap,
  editingDeck = null, // 編集中のデッキ（nullなら新規作成）
  onBack,
  onSave,
}) => {
  const isMobile = useIsMobile();

  // フィルタ状態
  const [filterAttribute, setFilterAttribute] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterRarity, setFilterRarity] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [effectSearch, setEffectSearch] = useState(''); // 効果テキスト検索

  // 詳細フィルター状態
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [filterKeywords, setFilterKeywords] = useState([]);
  const [keywordMode, setKeywordMode] = useState('or');
  const [filterCategories, setFilterCategories] = useState([]);
  const [categoryMode, setCategoryMode] = useState('or');
  const [filterCosts, setFilterCosts] = useState([]); // コストフィルター

  // 表示モード
  const [groupByCard, setGroupByCard] = useState(false); // レアリティをまとめて表示

  // ソート状態
  const [sortBy, setSortBy] = useState('cost');
  const [sortOrder, setSortOrder] = useState('asc');

  // デッキ状態
  const [deckName, setDeckName] = useState(editingDeck?.name || '新しいデッキ');
  const [deckCards, setDeckCards] = useState(editingDeck?.cards || []);
  // deckCards: [{ cardId, rarity }, ...]

  const [error, setError] = useState(null);

  // インポート/エクスポートモーダル
  const [showImportExportModal, setShowImportExportModal] = useState(false);
  const [importExportMode, setImportExportMode] = useState('export'); // 'export' | 'import'
  const [importText, setImportText] = useState('');
  const [importResult, setImportResult] = useState(null);

  // ホバー中のカード情報
  const [hoveredCard, setHoveredCard] = useState(null);
  // hoveredCard: { card, rarity, source: 'list' | 'deck' }

  // グリッドコンテナの高さ測定用
  const gridContainerRef = useRef(null);
  const [gridContainerHeight, setGridContainerHeight] = useState(500);

  // グリッドコンテナの高さを監視
  useEffect(() => {
    if (!gridContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) {
          setGridContainerHeight(height);
        }
      }
    });

    resizeObserver.observe(gridContainerRef.current);

    // 初期高さを設定
    const initialHeight = gridContainerRef.current.offsetHeight;
    if (initialHeight > 0) {
      setGridContainerHeight(initialHeight);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // 所持カード一覧を生成（カードID + レアリティでグループ化）
  const ownedCards = useMemo(() => {
    if (!playerData?.collection) return [];

    const cardMap = new Map();

    playerData.collection.forEach(entry => {
      const card = allCards.find(c => c.id === entry.cardId);
      if (!card) return;

      const key = `${entry.cardId}_${entry.rarity}`;
      cardMap.set(key, {
        card,
        cardId: entry.cardId,
        rarity: entry.rarity,
        quantity: entry.quantity,
      });
    });

    return Array.from(cardMap.values());
  }, [playerData, allCards]);

  // フィルタリングされたカード一覧
  const filteredCards = useMemo(() => {
    let result = ownedCards.filter(({ card, rarity }) => {
      if (filterAttribute !== 'all' && card.attribute !== filterAttribute) return false;
      if (filterType !== 'all' && card.type !== filterType) return false;
      if (filterRarity !== 'all' && rarity !== filterRarity) return false;
      if (searchText && !card.name.includes(searchText)) return false;

      // 効果テキスト検索
      if (effectSearch) {
        const searchTerm = effectSearch.toLowerCase();
        if (!card.effect?.toLowerCase().includes(searchTerm)) return false;
      }

      // コストフィルター
      if (filterCosts.length > 0) {
        const cardCost = card.cost || 0;
        if (!filterCosts.includes(cardCost)) return false;
      }

      // キーワードフィルター
      if (filterKeywords.length > 0) {
        if (!card.keyword) return false;
        if (keywordMode === 'and') {
          if (!filterKeywords.every((kw) => card.keyword.includes(kw))) return false;
        } else {
          if (!filterKeywords.some((kw) => card.keyword.includes(kw))) return false;
        }
      }

      // カテゴリフィルター
      if (filterCategories.length > 0) {
        if (!card.category) return false;
        const cardCategories = Array.isArray(card.category) ? card.category : [];
        if (categoryMode === 'and') {
          if (!filterCategories.every((cat) => cardCategories.includes(cat))) return false;
        } else {
          if (!filterCategories.some((cat) => cardCategories.includes(cat))) return false;
        }
      }

      return true;
    });

    // groupByCardモード: カードIDでまとめる
    if (groupByCard) {
      const groupedMap = new Map();
      result.forEach(entry => {
        const { cardId, card, rarity, quantity } = entry;
        if (!groupedMap.has(cardId)) {
          groupedMap.set(cardId, {
            card,
            cardId,
            rarities: [], // 所持しているレアリティ一覧
            totalQuantity: 0,
          });
        }
        const group = groupedMap.get(cardId);
        group.rarities.push({ rarity, quantity });
        group.totalQuantity += quantity;
      });

      // rarities をレアリティ高い順にソート
      groupedMap.forEach(group => {
        group.rarities.sort((a, b) => {
          return RARITIES.indexOf(b.rarity) - RARITIES.indexOf(a.rarity);
        });
        // 最高レアリティを設定（表示用）
        group.rarity = group.rarities[0]?.rarity || 'C';
        group.quantity = group.totalQuantity;
      });

      result = Array.from(groupedMap.values());
    }

    // ソート
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'cost':
          comparison = (a.card.cost || 0) - (b.card.cost || 0);
          break;
        case 'name':
          comparison = a.card.name.localeCompare(b.card.name);
          break;
        case 'attack':
          comparison = (a.card.attack || 0) - (b.card.attack || 0);
          break;
        case 'hp':
          comparison = (a.card.hp || 0) - (b.card.hp || 0);
          break;
        default:
          comparison = (a.card.cost || 0) - (b.card.cost || 0);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [ownedCards, filterAttribute, filterType, filterRarity, searchText, effectSearch, filterCosts, filterKeywords, keywordMode, filterCategories, categoryMode, groupByCard, sortBy, sortOrder]);

  // キーワードトグル
  const toggleKeyword = (keyword) => {
    setFilterKeywords(prev =>
      prev.includes(keyword)
        ? prev.filter(k => k !== keyword)
        : [...prev, keyword]
    );
  };

  // カテゴリトグル
  const toggleCategory = (category) => {
    setFilterCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // コストトグル
  const toggleCost = (cost) => {
    setFilterCosts(prev =>
      prev.includes(cost)
        ? prev.filter(c => c !== cost)
        : [...prev, cost]
    );
  };

  // フィルターリセット
  const resetFilters = () => {
    setFilterAttribute('all');
    setFilterType('all');
    setFilterRarity('all');
    setSearchText('');
    setEffectSearch('');
    setFilterKeywords([]);
    setFilterCategories([]);
    setFilterCosts([]);
    setKeywordMode('or');
    setCategoryMode('or');
  };

  // 詳細フィルターがアクティブかどうか
  const hasAdvancedFilters = filterKeywords.length > 0 || filterCategories.length > 0 || filterCosts.length > 0;

  // usedCounts Map: カードIDごとのデッキ内使用枚数
  const usedCounts = useMemo(() => {
    const map = new Map();
    deckCards.forEach(c => {
      map.set(c.cardId, (map.get(c.cardId) || 0) + 1);
    });
    return map;
  }, [deckCards]);

  // デッキ内のカード数をカウント（同名カード全体）
  const getCardCountInDeck = (cardId) => {
    return deckCards.filter(c => c.cardId === cardId).length;
  };

  // デッキ内の特定レアリティのカード数をカウント
  const getCardCountInDeckByRarity = (cardId, rarity) => {
    return deckCards.filter(c => c.cardId === cardId && c.rarity === rarity).length;
  };

  // デッキ内の禁忌カード数をカウント
  const getForbiddenCountInDeck = () => {
    return deckCards.filter(c => {
      const card = allCards.find(ac => ac.id === c.cardId);
      // isForbiddenフラグ、または keyword配列に '禁忌カード' が含まれるか確認
      return card?.isForbidden || (Array.isArray(card?.keyword) && card.keyword.includes('禁忌カード'));
    }).length;
  };

  // カードを追加できるか判定
  const canAddCard = (cardId, rarity, quantity) => {
    const card = allCards.find(c => c.id === cardId);
    if (!card) return { can: false, reason: 'カードが見つかりません' };

    // デッキ枚数上限
    if (deckCards.length >= DECK_SIZE) {
      return { can: false, reason: `デッキは${DECK_SIZE}枚までです` };
    }

    // 同名カード上限（レアリティ問わず全体で3枚まで）
    const currentCount = getCardCountInDeck(cardId);
    if (currentCount >= MAX_COPIES) {
      return { can: false, reason: `同名カードは${MAX_COPIES}枚までです` };
    }

    // 所持枚数チェック（特定レアリティの使用枚数 vs 所持枚数）
    const currentRarityCount = getCardCountInDeckByRarity(cardId, rarity);
    if (currentRarityCount >= quantity) {
      return { can: false, reason: 'このレアリティの所持枚数を超えています' };
    }

    // 禁忌カード上限
    const isForbidden = card.isForbidden || (Array.isArray(card.keyword) && card.keyword.includes('禁忌カード'));
    if (isForbidden && getForbiddenCountInDeck() >= MAX_FORBIDDEN) {
      return { can: false, reason: `禁忌カードは${MAX_FORBIDDEN}枚までです` };
    }

    return { can: true };
  };

  // DeckBuilderCardGrid用にカードデータを変換（通常モード用）
  const gridCards = useMemo(() => {
    if (groupByCard) return []; // まとめ表示モードでは使用しない
    return filteredCards.map(({ card, cardId, rarity, quantity }) => ({
      ...card,
      id: cardId,
      rarity,
      quantity,
    }));
  }, [filteredCards, groupByCard]);

  // DeckBuilderCardGrid用にカードデータを変換（まとめ表示モード用）
  const gridCardsGrouped = useMemo(() => {
    if (!groupByCard) return []; // 通常モードでは使用しない
    return filteredCards.map(({ card, cardId, rarity, rarities, totalQuantity }) => {
      // 追加可能かチェック（どれかのレアリティで追加可能か）
      const canAddAny = rarities?.some(({ rarity: r, quantity: q }) => canAddCard(cardId, r, q).can) || false;
      return {
        card,
        cardId,
        rarity,
        rarities: rarities || [],
        totalQuantity: totalQuantity || 0,
        canAdd: canAddAny,
      };
    });
  }, [filteredCards, groupByCard, deckCards]); // deckCardsが変わると追加可能判定が変わる

  // カードグリッドのクリックハンドラ（通常モード）
  const handleGridCardClick = useCallback((cardData) => {
    // カードを追加
    const { id: cardId, rarity, quantity } = cardData;
    const check = canAddCard(cardId, rarity, quantity);
    if (!check.can) {
      setError(check.reason);
      setTimeout(() => setError(null), 2000);
      return;
    }
    setDeckCards(prev => [...prev, { cardId, rarity }]);
    setError(null);
    // ホバー情報も更新
    const card = allCards.find(c => c.id === cardId);
    if (card) {
      setHoveredCard({ card, rarity, source: 'list' });
    }
  }, [allCards]);

  // カードグリッドのクリックハンドラ（まとめ表示モード）
  const handleGroupedCardClick = useCallback((cardId, rarities) => {
    // レアリティ高い順にソート済みのraritiesを使用して追加
    for (const { rarity, quantity } of rarities) {
      const check = canAddCard(cardId, rarity, quantity);
      if (check.can) {
        setDeckCards(prev => [...prev, { cardId, rarity }]);
        setError(null);
        // ホバー情報も更新
        const card = allCards.find(c => c.id === cardId);
        if (card) {
          setHoveredCard({ card, rarity, rarities, source: 'list' });
        }
        return;
      }
    }
    // 全レアリティで追加不可の場合
    const countInDeck = getCardCountInDeck(cardId);
    if (countInDeck >= MAX_COPIES) {
      setError(`同名カードは${MAX_COPIES}枚までです`);
    } else if (deckCards.length >= DECK_SIZE) {
      setError(`デッキは${DECK_SIZE}枚までです`);
    } else {
      setError('追加可能なレアリティがありません');
    }
    setTimeout(() => setError(null), 2000);
  }, [allCards, deckCards]);

  // カードグリッドのホバーハンドラ（通常モード）
  const handleGridCardHover = useCallback((cardData) => {
    const card = allCards.find(c => c.id === cardData.id);
    if (card) {
      setHoveredCard({ card, rarity: cardData.rarity || 'C', source: 'list' });
    }
  }, [allCards]);

  // カードグリッドのホバーハンドラ（まとめ表示モード）
  const handleGroupedCardHover = useCallback((data) => {
    setHoveredCard({
      card: data.card,
      rarity: data.rarity,
      rarities: data.rarities,
      source: 'list',
    });
  }, []);

  // カードをデッキに追加
  const addCardToDeck = (cardId, rarity, quantity) => {
    const check = canAddCard(cardId, rarity, quantity);
    if (!check.can) {
      setError(check.reason);
      setTimeout(() => setError(null), 2000);
      return;
    }

    setDeckCards(prev => [...prev, { cardId, rarity }]);
    setError(null);
  };

  // グループ表示モード用：レア度高い順に自動選択して追加
  const addCardFromGroup = (cardId, raritiesList) => {
    // レアリティ高い順にソート済みのraritiesListを使用
    for (const { rarity, quantity } of raritiesList) {
      const check = canAddCard(cardId, rarity, quantity);
      if (check.can) {
        setDeckCards(prev => [...prev, { cardId, rarity }]);
        setError(null);
        return;
      }
    }
    // 全レアリティで追加不可の場合
    const card = allCards.find(c => c.id === cardId);
    const countInDeck = getCardCountInDeck(cardId);
    if (countInDeck >= MAX_COPIES) {
      setError(`同名カードは${MAX_COPIES}枚までです`);
    } else if (deckCards.length >= DECK_SIZE) {
      setError(`デッキは${DECK_SIZE}枚までです`);
    } else {
      setError('追加可能なレアリティがありません');
    }
    setTimeout(() => setError(null), 2000);
  };

  // カードをデッキから削除
  const removeCardFromDeck = (index) => {
    setDeckCards(prev => prev.filter((_, i) => i !== index));
  };

  // デッキをクリア
  const clearDeck = () => {
    setDeckCards([]);
    setError(null);
  };

  // エクスポート: デッキをカードIDリストに変換
  const exportDeck = useCallback(() => {
    // カードIDをカンマ区切りで出力（レアリティは含めない）
    const cardIds = deckCards.map(entry => entry.cardId);
    return cardIds.join(',');
  }, [deckCards]);

  // インポート: テキストからカードIDを解析してデッキに追加
  const importDeck = useCallback((text) => {
    // テキストを解析: カンマ、スペース、改行で分割
    const cardIds = text
      .split(/[,\s\n]+/)
      .map(id => id.trim())
      .filter(id => id.startsWith('C') && id.length >= 8);

    if (cardIds.length === 0) {
      setImportResult({ success: false, message: '有効なカードIDが見つかりませんでした' });
      return;
    }

    // コレクションからカード情報を取得
    const collection = playerData?.collection || [];
    const newDeckCards = [];
    const errors = [];
    const cardCounts = {}; // 同名カード枚数カウント

    for (const cardId of cardIds) {
      // カードが存在するか確認
      const cardData = allCards.find(c => c.id === cardId);
      if (!cardData) {
        errors.push(`${cardId}: 存在しないカード`);
        continue;
      }

      // 同名カード制限チェック
      cardCounts[cardId] = (cardCounts[cardId] || 0) + 1;
      if (cardCounts[cardId] > MAX_COPIES) {
        errors.push(`${cardData.name}: 同名カードは${MAX_COPIES}枚まで`);
        continue;
      }

      // デッキ枚数制限チェック
      if (newDeckCards.length >= DECK_SIZE) {
        errors.push(`デッキ上限(${DECK_SIZE}枚)に達しました`);
        break;
      }

      // コレクションから最高レアリティを選択
      const ownedRarities = collection
        .filter(c => c.cardId === cardId && c.quantity > 0)
        .sort((a, b) => {
          const rarityOrder = ['GR', 'SP', 'ALT', 'SEC', 'HR', 'UR', 'SR', 'R', 'UC', 'C'];
          return rarityOrder.indexOf(a.rarity) - rarityOrder.indexOf(b.rarity);
        });

      if (ownedRarities.length === 0) {
        // コレクションに無い場合はCとして追加（所持していないカード）
        newDeckCards.push({ cardId, rarity: 'C' });
      } else {
        newDeckCards.push({ cardId, rarity: ownedRarities[0].rarity });
      }
    }

    // 結果を設定
    setDeckCards(newDeckCards);
    setImportResult({
      success: true,
      message: `${newDeckCards.length}枚のカードをインポートしました`,
      errors: errors.length > 0 ? errors.slice(0, 5) : null, // 最初の5つのエラーのみ表示
    });
  }, [allCards, playerData?.collection]);

  // インポート/エクスポートモーダルを開く
  const openExportModal = () => {
    setImportExportMode('export');
    setImportText('');
    setImportResult(null);
    setShowImportExportModal(true);
  };

  const openImportModal = () => {
    setImportExportMode('import');
    setImportText('');
    setImportResult(null);
    setShowImportExportModal(true);
  };

  // クリップボードにコピー
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setImportResult({ success: true, message: 'コピーしました!' });
    } catch (err) {
      setImportResult({ success: false, message: 'コピーに失敗しました' });
    }
  };

  // デッキを保存
  const saveDeck = () => {
    if (deckCards.length !== DECK_SIZE) {
      setError(`デッキは${DECK_SIZE}枚必要です（現在${deckCards.length}枚）`);
      return;
    }

    if (!deckName.trim()) {
      setError('デッキ名を入力してください');
      return;
    }

    const deck = {
      id: editingDeck?.id || `deck_${Date.now()}`,
      name: deckName.trim(),
      cards: deckCards,
      createdAt: editingDeck?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };

    onSave(deck);
  };

  // デッキ内カードをグループ化して表示用に整形
  const groupedDeckCards = useMemo(() => {
    const groups = new Map();

    deckCards.forEach((entry, index) => {
      const key = `${entry.cardId}_${entry.rarity}`;
      if (!groups.has(key)) {
        const card = allCards.find(c => c.id === entry.cardId);
        groups.set(key, {
          card,
          cardId: entry.cardId,
          rarity: entry.rarity,
          count: 0,
          indices: [],
        });
      }
      const group = groups.get(key);
      group.count++;
      group.indices.push(index);
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.card.cost !== b.card.cost) return a.card.cost - b.card.cost;
      return a.card.name.localeCompare(b.card.name);
    });
  }, [deckCards, allCards]);

  // 属性一覧
  const attributes = ['all', '炎', '水', '光', '闇', '原始', '未来', 'なし'];
  const types = ['all', 'monster', 'magic', 'field', 'phase'];
  const rarities = ['all', 'C', 'UC', 'R', 'SR', 'UR', 'HR', 'SEC', 'ALT', 'SP', 'GR'];

  // コスト分布計算（マナカーブ用）
  const costDistribution = useMemo(() => {
    // 0〜6と7+で分類
    const distribution = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, '7+': 0 };

    deckCards.forEach(entry => {
      const card = allCards.find(c => c.id === entry.cardId);
      if (!card) return;
      const cost = card.cost || 0;
      if (cost >= 7) {
        distribution['7+']++;
      } else {
        distribution[cost]++;
      }
    });

    // 最大枚数を計算（グラフの高さ正規化用）
    const maxCount = Math.max(...Object.values(distribution), 1);

    return { distribution, maxCount };
  }, [deckCards, allCards]);

  const canSave = deckCards.length === DECK_SIZE && deckName.trim();

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={{
        ...styles.header,
        padding: isMobile ? '10px 12px' : '12px 24px',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? '8px' : '0',
      }}>
        <div style={styles.headerLeft}>
          <button
            style={{
              ...styles.backButton,
              padding: isMobile ? '6px 12px' : '8px 16px',
              fontSize: isMobile ? '12px' : '14px',
            }}
            onClick={onBack}
            onMouseEnter={(e) => e.target.style.background = 'rgba(107,76,230,0.5)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(107,76,230,0.3)'}
          >
            ← 戻る
          </button>
          <span style={{ ...styles.title, fontSize: isMobile ? '18px' : '24px' }}>デッキ構築</span>
        </div>
        {/* マナカーブ（コスト分布） */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '3px',
          padding: '4px 12px',
          background: 'rgba(0,0,0,0.3)',
          borderRadius: '8px',
          height: isMobile ? '50px' : '60px',
        }}>
          {[0, 1, 2, 3, 4, 5, 6, '7+'].map((cost) => {
            const count = costDistribution.distribution[cost];
            const maxBarHeight = isMobile ? 28 : 35; // px
            // 枚数に比例した高さ（0枚は2px、1枚以上は最低4px）
            const barHeight = count > 0
              ? Math.max(Math.round((count / costDistribution.maxCount) * maxBarHeight), 4)
              : 2;

            return (
              <div
                key={cost}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: isMobile ? '18px' : '24px',
                }}
              >
                {/* 枚数（バーの上） */}
                <span style={{
                  fontSize: isMobile ? '9px' : '10px',
                  color: count > 0 ? '#fff' : '#606060',
                  fontWeight: 'bold',
                  marginBottom: '2px',
                  height: '12px',
                }}>
                  {count > 0 ? count : ''}
                </span>
                {/* バー */}
                <div style={{
                  width: '100%',
                  height: `${barHeight}px`,
                  background: count > 0
                    ? `linear-gradient(180deg,
                        ${cost <= 2 ? '#4ade80' : cost <= 4 ? '#facc15' : '#f87171'} 0%,
                        ${cost <= 2 ? '#22c55e' : cost <= 4 ? '#eab308' : '#ef4444'} 100%)`
                    : 'rgba(100,100,100,0.3)',
                  borderRadius: '2px 2px 0 0',
                  transition: 'height 0.3s ease',
                  boxShadow: count > 0 ? '0 0 4px rgba(255,255,255,0.2)' : 'none',
                }} />
                {/* コスト番号 */}
                <span style={{
                  fontSize: isMobile ? '8px' : '9px',
                  color: '#a0a0a0',
                  marginTop: '2px',
                }}>
                  {cost}
                </span>
              </div>
            );
          })}
        </div>

        <div style={styles.deckInfo}>
          <span style={{
            ...styles.deckCount,
            color: deckCards.length === DECK_SIZE ? '#6bff6b' : '#ff6b6b',
            fontSize: isMobile ? '14px' : '16px',
          }}>
            {deckCards.length} / {DECK_SIZE} 枚
          </span>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div style={{
        ...styles.mainContent,
        flexDirection: isMobile ? 'column' : 'row',
        padding: isMobile ? '12px' : '12px 24px',
        gap: isMobile ? '16px' : '24px',
        minHeight: 0, // flex childでスクロールを有効にするため
      }}>
        {/* 左側: カード一覧 */}
        <div style={{
          ...styles.cardListPanel,
          flex: isMobile ? 'none' : '1',
          width: isMobile ? '100%' : 'auto',
          maxHeight: isMobile ? '45vh' : 'none',
          minHeight: 0, // flex childでスクロールを有効にするため
        }}>
          {/* フィルタバー */}
          <div style={{
            ...styles.filterBar,
            padding: isMobile ? '8px' : '12px',
            gap: isMobile ? '6px' : '8px',
            flexDirection: 'column',
          }}>
            {/* 基本フィルター行 */}
            <div style={{ display: 'flex', gap: isMobile ? '6px' : '8px', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
              <select
                value={filterAttribute}
                onChange={(e) => setFilterAttribute(e.target.value)}
                style={{ ...styles.filterSelect, fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '4px 6px' : '6px 10px' }}
              >
                <option value="all">全属性</option>
                {attributes.slice(1).map(attr => (
                  <option key={attr} value={attr}>{attr}</option>
                ))}
              </select>

              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={{ ...styles.filterSelect, fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '4px 6px' : '6px 10px' }}
              >
                <option value="all">全タイプ</option>
                <option value="monster">モンスター</option>
                <option value="magic">魔法</option>
                <option value="field">フィールド</option>
                <option value="phase">フェイズ</option>
              </select>

              <select
                value={filterRarity}
                onChange={(e) => setFilterRarity(e.target.value)}
                style={{ ...styles.filterSelect, fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '4px 6px' : '6px 10px' }}
              >
                <option value="all">全レアリティ</option>
                {rarities.slice(1).map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>

              {!isMobile && (
                <>
                  <input
                    type="text"
                    placeholder="カード名で検索..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ ...styles.searchInput, flex: 1, minWidth: '100px' }}
                  />
                  <input
                    type="text"
                    placeholder="効果で検索..."
                    value={effectSearch}
                    onChange={(e) => setEffectSearch(e.target.value)}
                    style={{ ...styles.searchInput, flex: 1, minWidth: '100px' }}
                  />
                </>
              )}
            </div>

            {/* ソート・詳細フィルターボタン行 */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', color: '#a0a0a0' }}>並び替え:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{ ...styles.filterSelect, fontSize: '11px', padding: '4px 8px' }}
              >
                <option value="cost">コスト</option>
                <option value="name">名前</option>
                <option value="attack">攻撃力</option>
                <option value="hp">HP</option>
              </select>
              <button
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                style={{
                  ...styles.filterSelect,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  fontSize: '11px',
                }}
              >
                {sortOrder === 'asc' ? '▲' : '▼'}
              </button>

              {/* レアリティまとめ表示トグル */}
              <button
                onClick={() => setGroupByCard(!groupByCard)}
                style={{
                  ...styles.filterSelect,
                  cursor: 'pointer',
                  padding: '4px 10px',
                  fontSize: '11px',
                  background: groupByCard ? 'rgba(46,204,113,0.4)' : '#1a1a2e',
                  borderColor: groupByCard ? 'rgba(46,204,113,0.6)' : 'rgba(107,76,230,0.5)',
                }}
                title="同名カードをまとめて表示（レア度高い順に自動選択）"
              >
                {groupByCard ? '✓ まとめ表示' : 'まとめ表示'}
              </button>

              <button
                onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
                style={{
                  ...styles.filterSelect,
                  cursor: 'pointer',
                  padding: '4px 10px',
                  fontSize: '11px',
                  marginLeft: 'auto',
                  background: hasAdvancedFilters ? 'rgba(107,76,230,0.4)' : '#1a1a2e',
                }}
              >
                {showAdvancedFilter ? '▲ 詳細' : '▼ 詳細'} {hasAdvancedFilters && '(有効)'}
              </button>

              <button
                onClick={resetFilters}
                style={{
                  ...styles.filterSelect,
                  cursor: 'pointer',
                  padding: '4px 10px',
                  fontSize: '11px',
                  color: '#ff6b6b',
                  borderColor: 'rgba(255,100,100,0.5)',
                }}
              >
                リセット
              </button>
            </div>

            {/* 詳細フィルターパネル */}
            {showAdvancedFilter && (
              <div style={{
                width: '100%',
                marginTop: '8px',
                padding: '10px',
                background: 'rgba(15,15,35,0.6)',
                borderRadius: '6px',
                border: '1px solid rgba(107,76,230,0.3)',
                maxHeight: isMobile ? '30vh' : 'none',
                overflow: isMobile ? 'auto' : 'visible',
              }}>
                {/* モバイル用テキスト検索 */}
                {isMobile && (
                  <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      placeholder="カード名で検索..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ ...styles.searchInput, flex: 1, minWidth: '100px', fontSize: '12px', padding: '6px 10px' }}
                    />
                    <input
                      type="text"
                      placeholder="効果で検索..."
                      value={effectSearch}
                      onChange={(e) => setEffectSearch(e.target.value)}
                      style={{ ...styles.searchInput, flex: 1, minWidth: '100px', fontSize: '12px', padding: '6px 10px' }}
                    />
                  </div>
                )}

                {/* コストフィルター */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '11px', color: '#a0a0a0' }}>コスト:</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((cost) => (
                      <button
                        key={cost}
                        onClick={() => toggleCost(cost)}
                        style={{
                          ...styles.filterSelect,
                          fontSize: '10px',
                          padding: '3px 8px',
                          minWidth: '28px',
                          background: filterCosts.includes(cost) ? 'rgba(107,76,230,0.5)' : '#1a1a2e',
                        }}
                      >
                        {cost}
                      </button>
                    ))}
                  </div>
                </div>

                {/* キーワードフィルター */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '11px', color: '#a0a0a0' }}>キーワード:</span>
                    <button
                      onClick={() => setKeywordMode('or')}
                      style={{
                        ...styles.filterSelect,
                        fontSize: '9px',
                        padding: '2px 6px',
                        background: keywordMode === 'or' ? 'rgba(107,76,230,0.5)' : '#1a1a2e',
                      }}
                    >
                      OR
                    </button>
                    <button
                      onClick={() => setKeywordMode('and')}
                      style={{
                        ...styles.filterSelect,
                        fontSize: '9px',
                        padding: '2px 6px',
                        background: keywordMode === 'and' ? 'rgba(107,76,230,0.5)' : '#1a1a2e',
                      }}
                    >
                      AND
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {KEYWORDS.map((kw) => (
                      <button
                        key={kw}
                        onClick={() => toggleKeyword(kw)}
                        style={{
                          ...styles.filterSelect,
                          fontSize: '10px',
                          padding: '3px 6px',
                          background: filterKeywords.includes(kw) ? 'rgba(107,76,230,0.5)' : '#1a1a2e',
                        }}
                      >
                        {kw}
                      </button>
                    ))}
                  </div>
                </div>

                {/* カテゴリフィルター */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '11px', color: '#a0a0a0' }}>カテゴリ:</span>
                    <button
                      onClick={() => setCategoryMode('or')}
                      style={{
                        ...styles.filterSelect,
                        fontSize: '9px',
                        padding: '2px 6px',
                        background: categoryMode === 'or' ? 'rgba(107,76,230,0.5)' : '#1a1a2e',
                      }}
                    >
                      OR
                    </button>
                    <button
                      onClick={() => setCategoryMode('and')}
                      style={{
                        ...styles.filterSelect,
                        fontSize: '9px',
                        padding: '2px 6px',
                        background: categoryMode === 'and' ? 'rgba(107,76,230,0.5)' : '#1a1a2e',
                      }}
                    >
                      AND
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {CARD_CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => toggleCategory(cat)}
                        style={{
                          ...styles.filterSelect,
                          fontSize: '10px',
                          padding: '3px 6px',
                          background: filterCategories.includes(cat) ? 'rgba(107,76,230,0.5)' : '#1a1a2e',
                        }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* カードグリッド（両モードで仮想スクロール使用） */}
          <div ref={gridContainerRef} style={{ flex: 1, minHeight: 0 }}>
            <DeckBuilderCardGrid
              cards={groupByCard ? undefined : gridCards}
              groupedCards={groupByCard ? gridCardsGrouped : undefined}
              grouped={groupByCard}
              usedCounts={usedCounts}
              onCardClick={groupByCard ? undefined : handleGridCardClick}
              onGroupedCardClick={groupByCard ? handleGroupedCardClick : undefined}
              onCardHover={groupByCard ? undefined : handleGridCardHover}
              onGroupedCardHover={groupByCard ? handleGroupedCardHover : undefined}
              effectLevel={EFFECT_LEVELS.MINIMAL}
              isMobile={isMobile}
              height={isMobile ? 300 : gridContainerHeight}
              emptyMessage="条件に一致するカードがありません"
            />
          </div>
        </div>

        {/* 右側: デッキ内容 */}
        <div style={{
          ...styles.deckPanel,
          width: isMobile ? '100%' : '320px',
          maxHeight: isMobile ? '45vh' : 'none',
          minHeight: 0, // flex childでスクロールを有効にするため
        }}>
          <div style={{
            ...styles.deckHeader,
            padding: isMobile ? '8px' : '12px',
          }}>
            <input
              type="text"
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
              placeholder="デッキ名"
              style={{ ...styles.deckNameInput, fontSize: isMobile ? '14px' : '16px' }}
            />
            <div style={{ ...styles.deckStats, fontSize: isMobile ? '11px' : '12px' }}>
              <span>モンスター: {deckCards.filter(c => allCards.find(ac => ac.id === c.cardId)?.type === 'monster').length}</span>
              <span>魔法: {deckCards.filter(c => allCards.find(ac => ac.id === c.cardId)?.type === 'magic').length}</span>
              <span>その他: {deckCards.filter(c => {
                const card = allCards.find(ac => ac.id === c.cardId);
                return card?.type !== 'monster' && card?.type !== 'magic';
              }).length}</span>
            </div>
            {error && (
              <div style={styles.errorMessage}>{error}</div>
            )}
          </div>

          <div style={styles.deckList}>
            {groupedDeckCards.length === 0 ? (
              <div style={styles.emptyMessage}>
                左のカード一覧からカードを選択してください
              </div>
            ) : (
              groupedDeckCards.map(({ card, cardId, rarity, count, indices }) => {
                const colors = ATTRIBUTE_COLORS[card.attribute] || ATTRIBUTE_COLORS['なし'];
                const rarityColor = RARITY_COLORS[rarity] || '#808080';

                return (
                  <div
                    key={`${cardId}_${rarity}`}
                    style={{
                      ...styles.deckCardItem,
                      background: `${colors.bg}80`,
                    }}
                    onClick={() => removeCardFromDeck(indices[indices.length - 1])}
                    onMouseEnter={() => setHoveredCard({ card, rarity, source: 'deck' })}
                    title="クリックで1枚削除"
                  >
                    <div style={styles.deckCardCost}>{card.cost}</div>
                    <div style={styles.deckCardName}>{card.name}</div>
                    <div style={{
                      ...styles.deckCardRarity,
                      background: rarityColor,
                    }}>
                      {rarity}
                    </div>
                    <div style={styles.deckCardCount}>×{count}</div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{
            ...styles.deckFooter,
            flexWrap: 'wrap',
          }}>
            {/* インポート/エクスポートボタン */}
            <button
              style={{
                ...styles.clearButton,
                background: 'rgba(78,205,196,0.2)',
                color: '#4ecdc4',
                borderColor: 'rgba(78,205,196,0.5)',
                fontSize: '12px',
                padding: '8px 10px',
              }}
              onClick={openImportModal}
            >
              インポート
            </button>
            <button
              style={{
                ...styles.clearButton,
                background: 'rgba(78,205,196,0.2)',
                color: '#4ecdc4',
                borderColor: 'rgba(78,205,196,0.5)',
                fontSize: '12px',
                padding: '8px 10px',
              }}
              onClick={openExportModal}
            >
              エクスポート
            </button>
            <div style={{ flex: 1 }} />
            <button
              style={styles.clearButton}
              onClick={clearDeck}
            >
              クリア
            </button>
            <button
              style={{
                ...styles.saveButton,
                ...(canSave ? {} : styles.saveButtonDisabled),
              }}
              onClick={saveDeck}
              disabled={!canSave}
            >
              保存
            </button>
          </div>
        </div>

        {/* 右側: カード詳細パネル（デスクトップのみ通常表示） */}
        {!isMobile && (
          <div style={{
            ...styles.detailPanel,
            minHeight: 0, // flex childでスクロールを有効にするため
          }}>
            {hoveredCard ? (
              <div style={styles.detailContent}>
              {/* カードプレビュー */}
              {(() => {
                const { card, rarity } = hoveredCard;
                const colors = ATTRIBUTE_COLORS[card.attribute] || ATTRIBUTE_COLORS['なし'];
                const rarityColor = RARITY_COLORS[rarity] || '#808080';
                const isMonster = card.type === 'monster';
                const isForbidden = card.isForbidden || (Array.isArray(card.keyword) && card.keyword.includes('禁忌カード'));

                return (
                  <>
                    {/* カードプレビュー（CardV2 + RarityEffectLayer） */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}>
                      <RarityEffectLayer
                        rarity={rarity}
                        effectLevel="minimal"
                        width={180}
                        height={234}
                        enableMouseEffect={true}
                      >
                        <CardV2
                          card={card}
                          displayMode="detail"
                          displayWidth={180}
                        />
                      </RarityEffectLayer>
                    </div>

                    {/* レアリティバッジ */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '12px',
                    }}>
                      <div style={{
                        padding: '4px 16px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        color: '#fff',
                        background: rarityColor,
                        boxShadow: `0 0 10px ${rarityColor}60`,
                      }}>
                        {RARITY_NAMES[rarity] || rarity}
                      </div>
                    </div>

                    {/* 基本情報 */}
                    <div style={styles.detailSection}>
                      <div style={styles.detailSectionTitle}>基本情報</div>
                      <div style={styles.detailStatsRow}>
                        <div style={styles.detailStatItem}>
                          <span style={styles.detailStatLabel}>属性:</span>
                          <span style={{...styles.detailStatValue, color: colors.glow || '#fff'}}>{card.attribute}</span>
                        </div>
                        <div style={styles.detailStatItem}>
                          <span style={styles.detailStatLabel}>タイプ:</span>
                          <span style={styles.detailStatValue}>
                            {card.type === 'monster' ? 'モンスター' :
                             card.type === 'magic' ? '魔法' :
                             card.type === 'field' ? 'フィールド' : 'フェイズ'}
                          </span>
                        </div>
                      </div>
                      {/* モンスターのカテゴリ */}
                      {isMonster && card.category && (
                        <div style={{ marginBottom: '8px' }}>
                          <span style={styles.detailStatLabel}>カテゴリ:</span>
                          <span style={{ ...styles.detailStatValue, marginLeft: '6px', fontSize: '13px' }}>
                            {card.category}
                          </span>
                        </div>
                      )}
                      {/* キーワード能力 */}
                      {(() => {
                        const keywords = getCardKeywords(card);
                        if (keywords.length === 0) return null;
                        return (
                          <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                            <span style={{ ...styles.detailStatLabel, marginRight: '4px' }}>キーワード:</span>
                            {keywords.map((kw, i) => (
                              <span key={i} style={styles.detailKeyword}>
                                {kw}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                      {isMonster && (
                        <div style={styles.detailStatsRow}>
                          <div style={styles.detailStatItem}>
                            <span style={styles.detailStatLabel}>ATK:</span>
                            <span style={{...styles.detailStatValue, color: '#ff6b6b'}}>{card.attack}</span>
                          </div>
                          <div style={styles.detailStatItem}>
                            <span style={styles.detailStatLabel}>HP:</span>
                            <span style={{...styles.detailStatValue, color: '#4ade80'}}>{card.hp}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* キーワード・禁忌 */}
                    {(card.keyword || isForbidden) && (
                      <div style={styles.detailSection}>
                        {isForbidden && (
                          <span style={styles.detailForbidden}>禁忌カード</span>
                        )}
                        {card.keyword && typeof card.keyword === 'string' && card.keyword.split('】').filter(k => k).map((keyword, i) => (
                          <span key={i} style={styles.detailKeyword}>
                            {keyword.replace('【', '')}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* 効果テキスト */}
                    {card.effect && (
                      <div style={styles.detailSection}>
                        <div style={styles.detailSectionTitle}>効果</div>
                        <div style={styles.detailEffectText}>{card.effect}</div>
                      </div>
                    )}

                    {/* フレーバーテキスト */}
                    {card.flavor && (
                      <div style={styles.detailSection}>
                        <div style={styles.detailSectionTitle}>フレーバー</div>
                        <div style={{
                          ...styles.detailEffectText,
                          fontStyle: 'italic',
                          color: '#a0a0a0',
                        }}>
                          {card.flavor}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            ) : (
              <div style={styles.detailEmpty}>
                カードにマウスを<br />
                合わせると詳細を表示
              </div>
            )}
          </div>
        )}
      </div>

      {/* モバイル用: カード詳細フローティングパネル */}
      {isMobile && hoveredCard && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          maxHeight: '50vh',
          background: 'rgba(15,15,35,0.98)',
          borderTop: '2px solid #6b4ce6',
          borderRadius: '16px 16px 0 0',
          boxShadow: '0 -4px 20px rgba(107,76,230,0.4)',
          zIndex: 1000,
          overflow: 'auto',
          animation: 'slideUp 0.3s ease',
        }}>
          {/* 閉じるボタン */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderBottom: '1px solid rgba(107,76,230,0.3)',
            position: 'sticky',
            top: 0,
            background: 'rgba(15,15,35,0.98)',
            zIndex: 1,
          }}>
            <span style={{ fontSize: '12px', color: '#a78bfa' }}>カード詳細</span>
            <button
              onClick={() => setHoveredCard(null)}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: 'none',
                background: 'rgba(255,100,100,0.3)',
                color: '#ff6b6b',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              閉じる
            </button>
          </div>
          <div style={{ padding: '12px' }}>
            {(() => {
              const { card, rarity } = hoveredCard;
              const colors = ATTRIBUTE_COLORS[card.attribute] || ATTRIBUTE_COLORS['なし'];
              const rarityColor = RARITY_COLORS[rarity] || '#808080';
              const isMonster = card.type === 'monster';
              const isForbidden = card.isForbidden || (Array.isArray(card.keyword) && card.keyword.includes('禁忌カード'));

              return (
                <>
                  {/* コンパクトなカード情報 */}
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <div style={{
                      width: '80px',
                      height: '100px',
                      borderRadius: '6px',
                      background: colors.bg,
                      border: `2px solid ${rarityColor}`,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#fff' }}>{card.name}</div>
                      {isMonster && (
                        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                          {card.attack}/{card.hp}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>
                        {card.name}
                      </div>
                      <div style={{ fontSize: '11px', color: '#a0a0a0', marginBottom: '4px' }}>
                        コスト: {card.cost} | {card.attribute} |
                        {card.type === 'monster' ? 'モンスター' : card.type === 'magic' ? '魔法' : card.type === 'field' ? 'フィールド' : 'フェイズ'}
                      </div>
                      {isMonster && (
                        <div style={{ fontSize: '11px', color: '#a0a0a0', marginBottom: '4px' }}>
                          ATK: <span style={{ color: '#ff6b6b' }}>{card.attack}</span> |
                          HP: <span style={{ color: '#4ade80' }}>{card.hp}</span>
                        </div>
                      )}
                      {isMonster && card.category && (
                        <div style={{ fontSize: '11px', color: '#a0a0a0' }}>
                          カテゴリ: {card.category}
                        </div>
                      )}
                      {(() => {
                        const keywords = getCardKeywords(card);
                        if (keywords.length === 0) return null;
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                            {keywords.map((kw, i) => (
                              <span key={i} style={{
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: 'rgba(157,76,230,0.3)',
                                color: '#c9a0ff',
                                fontSize: '10px',
                              }}>
                                {kw}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  {/* 効果テキスト */}
                  {card.effect && (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#a0a0a0', marginBottom: '4px' }}>効果</div>
                      <div style={{ fontSize: '11px', color: '#e0e0e0', lineHeight: '1.5' }}>{card.effect}</div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* CSS Animation for mobile panel */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>

      {/* インポート/エクスポートモーダル */}
      {showImportExportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setShowImportExportModal(false)}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1a1a3a 0%, #2a2a4a 100%)',
              borderRadius: '12px',
              padding: '24px',
              border: '2px solid #6b4ce6',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#4ecdc4',
              }}>
                {importExportMode === 'export' ? 'デッキをエクスポート' : 'デッキをインポート'}
              </h3>
              <button
                onClick={() => setShowImportExportModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#a0a0a0',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 8px',
                }}
              >
                ×
              </button>
            </div>

            {importExportMode === 'export' ? (
              <>
                <p style={{ fontSize: '13px', color: '#a0a0a0', marginBottom: '12px' }}>
                  デッキのカードIDリストをコピーして共有できます。
                </p>
                <textarea
                  readOnly
                  value={exportDeck()}
                  style={{
                    width: '100%',
                    height: '150px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(107,76,230,0.5)',
                    background: '#0a0a1a',
                    color: '#e0e0e0',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    resize: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button
                    onClick={() => copyToClipboard(exportDeck())}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    コピー
                  </button>
                  <button
                    onClick={() => setShowImportExportModal(false)}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: '1px solid rgba(107,76,230,0.5)',
                      background: 'rgba(107,76,230,0.2)',
                      color: '#a78bfa',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    閉じる
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: '13px', color: '#a0a0a0', marginBottom: '12px' }}>
                  カードIDをカンマ区切りで入力してください。<br />
                  コレクションから最高レアリティが自動選択されます。
                </p>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="C0000001,C0000002,C0000003,..."
                  style={{
                    width: '100%',
                    height: '150px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(107,76,230,0.5)',
                    background: '#0a0a1a',
                    color: '#e0e0e0',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    resize: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button
                    onClick={() => {
                      importDeck(importText);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    インポート
                  </button>
                  <button
                    onClick={() => setShowImportExportModal(false)}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: '1px solid rgba(107,76,230,0.5)',
                      background: 'rgba(107,76,230,0.2)',
                      color: '#a78bfa',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    閉じる
                  </button>
                </div>
              </>
            )}

            {/* 結果表示 */}
            {importResult && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                borderRadius: '8px',
                background: importResult.success ? 'rgba(78,205,196,0.2)' : 'rgba(255,100,100,0.2)',
                border: `1px solid ${importResult.success ? 'rgba(78,205,196,0.5)' : 'rgba(255,100,100,0.5)'}`,
              }}>
                <div style={{
                  fontSize: '13px',
                  color: importResult.success ? '#4ecdc4' : '#ff6b6b',
                  fontWeight: 'bold',
                }}>
                  {importResult.message}
                </div>
                {importResult.errors && (
                  <div style={{
                    fontSize: '11px',
                    color: '#ff6b6b',
                    marginTop: '8px',
                  }}>
                    {importResult.errors.map((err, i) => (
                      <div key={i}>・{err}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckBuilder;
